/**
 * Main App component with routing.
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { LoginPage } from './pages/LoginPage';
import { SignUpPage } from './pages/SignUpPage';
import { PreferencesPage } from './pages/PreferencesPage';
import { ProfilePage } from './pages/ProfilePage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { VMDetailPage } from './pages/VMDetailPage';
import { ClusterDetailPage } from './pages/ClusterDetailPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { UserManagementPage } from './pages/UserManagementPage';
import { SettingsPage } from './pages/SettingsPage';
import { OpenStackSettingsPage } from './pages/OpenStackSettingsPage';
import { KubernetesSettingsPage } from './pages/KubernetesSettingsPage';
import { LocalLoginPage } from './pages/LocalLoginPage';
import { OAuthLoginPage } from './pages/OAuthLoginPage';
import { useSettings } from './contexts/SettingsContext';
import './App.css';

function AppContent() {
  const { settings } = useSettings();
  const appName = settings?.app_name || 'dasher';
  
  // Update document title when app name changes
  React.useEffect(() => {
    document.title = appName;
  }, [appName]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <PreferencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/clusters/:clusterName"
          element={
            <ProtectedRoute>
              <ClusterDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/vms/:vmId"
          element={
            <ProtectedRoute>
              <VMDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UserManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/openstack"
          element={
            <AdminRoute>
              <OpenStackSettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/kubernetes"
          element={
            <AdminRoute>
              <KubernetesSettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/local-login"
          element={
            <AdminRoute>
              <LocalLoginPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/oauth-login"
          element={
            <AdminRoute>
              <OAuthLoginPage />
            </AdminRoute>
          }
        />
        <Route path="/" element={<Navigate to="/projects" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <SettingsProvider>
      <ThemeProvider>
        <AuthProvider>
          <WebSocketProvider>
            <AppContent />
          </WebSocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </SettingsProvider>
  );
}

export default App;
